import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_PATH } from 'src/environments/environment';
import { BaridClientService } from '../barid/barid-client.service';
import { BaridAgentError } from '../barid/barid-contracts';
import { OFFLINE_LOCAL_PREFIX, OFFLINE_ROUTE_WHITELIST, OfflineRoute, findOfflineRoute } from './offline-route-whitelist';
import { OfflineStateService } from './offline-state.service';

function newOpId(): string {
  const cryptoRef = (window as any).crypto;
  return typeof cryptoRef?.randomUUID === 'function'
    ? cryptoRef.randomUUID()
    : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Caminho relativo a API (sem origem, sem query, sem barra inicial); `null` se a URL nao e da API do Wazir. */
export function relativeApiPath(url: string, apiPath: string = API_PATH): string | null {
  const base = apiPath.replace(/\/+$/, '');
  if (!url.startsWith(`${base}/`)) return null;
  return url.slice(base.length + 1).split('?')[0].split('#')[0];
}

/**
 * Com a chave OFFLINE_EDGE ligada E o Barid em OFFLINE, desvia SO as rotas da whitelist para o Barid local
 * (`/offline/local/<rota>`), com `X-Op-Id` nas escritas (dedupe). Fora disso (chave OFF, Barid ausente, modo
 * ONLINE/DEGRADED/RESYNCING, rota fora da whitelist - e a whitelist nasce VAZIA) a requisicao segue intacta:
 * zero mudanca de comportamento. Fica ANTES do AuthInterceptor e nao chama `next`, entao o JWT do Wazir nunca
 * chega ao agente.
 */
@Injectable()
export class OfflineInterceptor implements HttpInterceptor {
  /** Trocavel em testes. */
  whitelist: ReadonlyArray<OfflineRoute> = OFFLINE_ROUTE_WHITELIST;

  constructor(
    private readonly state: OfflineStateService,
    private readonly agent: BaridClientService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.whitelist.length || !this.state.routingActive) return next.handle(req);
    const relative = relativeApiPath(req.url);
    const route = relative === null ? null : findOfflineRoute(req.method, relative, this.whitelist);
    if (!route || relative === null) return next.handle(req);

    const method = req.method.toUpperCase() as OfflineRoute['method'];
    const extraHeaders: Record<string, string> = method === 'GET' ? {} : { 'X-Op-Id': req.headers.get('X-Op-Id') || newOpId() };
    const query = req.params.toString();
    const path = `${OFFLINE_LOCAL_PREFIX}${relative}${query ? `?${query}` : ''}`;
    return from(this.agent.offlineCall<unknown>(method, path, method === 'GET' ? undefined : req.body, extraHeaders)).pipe(
      map((body) => new HttpResponse({ status: 200, body, url: req.url })),
      catchError((error: unknown) => {
        const agentError = error as Partial<BaridAgentError> & { status?: number };
        return throwError(
          () =>
            new HttpErrorResponse({
              status: agentError?.status ?? 503,
              url: req.url,
              error: { code: agentError?.code ?? 'OFFLINE_LOCAL_UNAVAILABLE', message: (error as Error)?.message }
            })
        );
      })
    );
  }
}
